import { getLocaleDirection, registerLocaleData } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { KEY_LANGUAGE_STORAGE, languagesList } from './utils';

export type TDirection = 'rtl' | 'ltr';

@Injectable({
  providedIn: 'root'
})
export class DictionaryService {
  themeDirection$: BehaviorSubject<TDirection> =
    new BehaviorSubject<TDirection>('ltr');

  lang$: BehaviorSubject<string> = new BehaviorSubject<string>('en');

  constructor(private ts: TranslateService) {}

  async initLanguage(): Promise<void> {
    const lang = this.getUserLanguage();
    this.ts.setDefaultLang(lang);
    await this.changeLanguage(lang);
  }

  /**
   * 
   * @param lang must be like en-GB
   */
  async changeLanguage(lang: string) {
    const langCode = lang.substring(0, 2);

    let module = await this.loadLocale(langCode);
    document.documentElement.lang = langCode;

    registerLocaleData(module.default);

    import(`../../assets/i18n/${langCode}.json`).then((res) => {
      this.ts.setTranslation(langCode, res);
      this.ts.use(langCode);
      localStorage.setItem(KEY_LANGUAGE_STORAGE, langCode);

      try {
        const dir = getLocaleDirection(langCode);
        document.documentElement.dir = dir;
        this.themeDirection$.next(dir);
      } catch (err) {
        const dir = getLocaleDirection('en');
        document.documentElement.dir = dir;
        this.themeDirection$.next(dir);
      }

      this.lang$.next(langCode);
    });
  }

  private getUserLanguage(): string {
    return localStorage.getItem(KEY_LANGUAGE_STORAGE) ||
      this.getHandledlanguage( this.ts.getBrowserLang()!.substring(0, 2) );
  }

  private getHandledlanguage(lang: string | undefined | null) {
    return lang && languagesList.findIndex( e => e.lang === lang ) > -1 ? lang : 'en';
  }

  private loadLocale(locale: string) {
    switch (locale) {
      case 'ar': return import('@angular/common/locales/ar');
      case 'bg': return import('@angular/common/locales/bg');
      case 'cs': return import('@angular/common/locales/cs');
      case 'de': return import('@angular/common/locales/de');
      case 'da': return import('@angular/common/locales/da');
      case 'el': return import('@angular/common/locales/el');
      case 'en': return import('@angular/common/locales/en');
      case 'es': return import('@angular/common/locales/es');
      case 'fi': return import('@angular/common/locales/fi');
      case 'fr': return import('@angular/common/locales/fr');
      case 'he': return import('@angular/common/locales/he');
      case 'hr': return import('@angular/common/locales/hr');
      case 'hu': return import('@angular/common/locales/hu');
      case 'it': return import('@angular/common/locales/it');
      case 'ko': return import('@angular/common/locales/ko');
      case 'lv': return import('@angular/common/locales/lv');
      case 'nl': return import('@angular/common/locales/nl');
      case 'nn': return import('@angular/common/locales/nn');
      case 'pl': return import('@angular/common/locales/pl');
      case 'pt': return import('@angular/common/locales/pt');
      case 'ro': return import('@angular/common/locales/ro');
      case 'ru': return import('@angular/common/locales/ru');
      case 'sl': return import('@angular/common/locales/sl');
      case 'sr': return import('@angular/common/locales/sr');
      case 'sv': return import('@angular/common/locales/sv');
      case 'tr': return import('@angular/common/locales/tr');
      case 'uk': return import('@angular/common/locales/uk');
      default: return import('@angular/common/locales/en');
    }
  }

}
